import { MobileOnlyLogo } from '@/pages/components/Branding';
import { AddDiscountCode } from '@/pages/components/discount-code/AddDiscountCode';
import { Dialog, DialogContent, IconButton } from '@montugroup/design-system';
import type { Step } from '@montugroup/form-builder';
import type { RadioType } from '@montugroup/form-builder/FormType';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Link, Typography } from '@mui/material';
import { useState } from 'react';

const RadioElementType = {
  type: 'radio',
  values: [
    { id: 1, label: 'Yes', value: 'Yes' },
    { id: 2, label: 'No', value: 'No' },
  ],
} as RadioType;

const PrivacyModal = () => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  return (
    <Box sx={{ my: 2 }}>
      <Link sx={{ fontWeight: 700 }} onClick={handleOpen}>
        Privacy Collection Notice
      </Link>
      <Dialog open={open} onClose={handleClose}>
        <Box sx={{ p: { md: '1rem 1.5rem' } }}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <Typography variant="h6" component="h2">
              Privacy Collection Notice
            </Typography>
            <Typography>
              We are committed to ensuring your privacy is protected through compliance with Australian privacy laws
              that govern the collection, use, sharing, storage and disposal of personal information.
            </Typography>
            <Typography variant="h6" component="h2" sx={{ mt: 8 }}>
              Consent
            </Typography>
            <Typography sx={{ mt: 2 }}>
              Consent We collect Personal Information to enable us to provide our Services to you including our Clinic
              Services and support to supply Medicine. You consent to this by using our Platform. For more detailed
              information, please review our{' '}
              <Link href="https://alternaleaf.com.au/privacy" target="_blank" rel="noopener">
                Privacy Policy
              </Link>
              .
            </Typography>
          </DialogContent>
        </Box>
      </Dialog>
    </Box>
  );
};

export const preScreeningSteps: Step[] = [
  {
    id: 1,
    formTitle: <MobileOnlyLogo />,
    type: 'section',
    title: "Let's complete your pre-screening.",
    pageTitle: 'Start Pre-screening',
    nextButtonConfig: {
      text: 'Get Started',
      metadata: {
        id: 'sounds-good-btn',
      },
    },
    trackingData: {
      event: 'prescreening_start',
      stepName: 'Begin Screening',
      clickId: 'sounds-good-btn',
    },
    content: [
      {
        type: 'custom',
        component: (
          <Typography sx={{ my: (theme) => theme.spacing(5) }}>
            These questions help assess your eligibility for a nurse consultation. Being deemed eligible does not mean
            you will be prescribed medication.
          </Typography>
        ),
      },
      {
        type: 'custom',
        component: (
          <Typography sx={{ my: (theme) => theme.spacing(5) }}>The information you provide is confidential.</Typography>
        ),
      },
    ],
  },
  {
    id: 2,
    formTitle: <MobileOnlyLogo />,
    qid: 'medical_condition',
    type: 'question',
    pageTitle: 'Medical History',
    subtitle: '###### Firstly, do you have a medical condition that has lasted for longer than 3 months?',
    legend: 'Medical Condition:',
    nextButtonConfig: {
      text: 'Next',
      metadata: {
        id: 'medical-condition',
      },
    },
    trackingData: {
      event: 'prescreening_progress',
      stepName: 'Specify medical condition',
      clickId: 'medical-condition',
    },
    content: [RadioElementType],
    skipTo: (nextStepIndex: number, values: { [key: string]: string }) => {
      if (values['medical_condition'] === 'No') {
        return 8;
      }
      return nextStepIndex;
    },
  },
  {
    id: 3,
    formTitle: <MobileOnlyLogo />,
    qid: 'tried_treatment',
    type: 'question',
    pageTitle: 'Attempted Treatment History',
    subtitle: '###### Have you previously attempted to treat this condition?',
    legend: 'Attempted Treatment Options:',
    nextButtonConfig: {
      text: 'Next',
      metadata: {
        id: 'condition-treatment',
      },
    },
    trackingData: {
      event: 'prescreening_progress',
      stepName: 'Treatment history outcome',
      clickId: 'condition-treatment',
    },
    content: [RadioElementType],
    skipTo: (nextStepIndex: number, values: { [key: string]: string }) => {
      if (values['tried_treatment'] === 'No') {
        return 8;
      }
      return nextStepIndex;
    },
  },
  {
    id: 4,
    formTitle: <MobileOnlyLogo />,
    qid: 'treatment',
    type: 'question',
    pageTitle: 'Failed Treatment History',
    subtitle:
      '###### Now a little more about your treatment. Did previous treatment(s) fail to provide full relief or cause unwanted side effects?',
    legend: 'Failed Treatment Details Options:',
    nextButtonConfig: {
      text: 'Next',
      metadata: {
        id: 'treatment-effects',
      },
    },
    trackingData: {
      event: 'prescreening_progress',
      stepName: 'Treatment history outcome details',
      clickId: 'treatment-effects',
    },
    content: [RadioElementType],
    skipTo: (nextStepIndex: number, values: { [key: string]: string }) => {
      if (values['treatment'] === 'No') {
        return 8;
      }
      return nextStepIndex;
    },
  },
  {
    id: 5,
    formTitle: <MobileOnlyLogo />,
    qid: 'pregnant',
    type: 'question',
    pageTitle: 'Pregnancy Status',
    subtitle: '###### And to check, are you currently pregnant, breastfeeding or trying to conceive?',
    legend: 'Pregnancy Status:',
    nextButtonConfig: {
      text: 'Next',
      metadata: {
        id: 'pregnant',
      },
    },
    trackingData: {
      event: 'prescreening_progress',
      stepName: 'Pregnancy status',
      clickId: 'pregnant',
    },
    content: [RadioElementType],
    skipTo: (nextStepIndex: number, values: { [key: string]: string }) => {
      if (values['pregnant'] === 'Yes') {
        return 8;
      }
      return nextStepIndex;
    },
  },
  {
    id: 6,
    formTitle: <MobileOnlyLogo />,
    qid: 'psychosis',
    type: 'question',
    pageTitle: 'Mental Health History',
    subtitle:
      '###### Do you have a history of psychosis, or have you ever been diagnosed with schizophrenia or bipolar type 1 or 2?',
    legend: 'Mental Health History:',
    nextButtonConfig: {
      text: 'Next',
      metadata: {
        id: 'psychosis',
      },
    },
    trackingData: {
      event: 'prescreening_progress',
      stepName: 'Mental health history',
      clickId: 'psychosis',
    },
    content: [RadioElementType],
    skipTo: (nextStepIndex: number, values: { [key: string]: string }) => {
      if (values['psychosis'] === 'Yes') {
        return 8;
      }
      return nextStepIndex;
    },
  },
  {
    id: 7,
    formTitle: <MobileOnlyLogo />,
    qid: 'email_and_privacy',
    type: 'question',
    pageTitle: 'Email Consent',
    title: 'Just one last thing!',
    subtitle: 'Before we show you your results, please enter your email address.',
    fieldArray: true,
    isLastPage: true,
    nextButtonConfig: {
      text: 'Show my results',
      metadata: {
        id: 'email-privacy',
      },
    },
    trackingData: {
      event: 'prescreening_progress',
      stepName: 'Marketing consent',
      clickId: 'email-privacy',
    },
    content: [
      {
        name: 'email',
        type: 'email',
        label: 'Email Address',
        placeholder: 'name@example.com',
        autofill: 'biggie.smalls@notorious.big',
      },
      {
        type: 'custom',
        name: 'privacy_modal',
        component: <PrivacyModal />,
      },
      {
        name: 'accept_privacy_notice',
        type: 'checkbox',
        label:
          'I understand that all information I provide will be treated in accordance with the **[Alternaleaf privacy policy](https://alternaleaf.com.au/privacy)**.',
        checked: true,
        required: true,
      },
      {
        name: 'subscribe_product_services',
        type: 'checkbox',
        label: 'Please keep me updated about Alternaleaf’s services.',
        checked: true,
      },
    ],
  },
  {
    id: 8,
    formTitle: <MobileOnlyLogo />,
    type: 'section',
    pageTitle: 'Eligibility Result',
    shouldSave: true,
    content: [
      {
        type: 'custom',
        component: (
          <>
            <CheckCircleIcon color="success" sx={{ fontSize: 30 }} />
            <Typography sx={{ mb: (theme) => theme.spacing(5) }}>
              Thank you! Based on your provided information, we can confirm that you are <strong>ELIGIBLE</strong> for
              an Alternaleaf nurse consultation.
            </Typography>
          </>
        ),
      },
      {
        type: 'text',
        value: "You're one click away from booking your appointment with Alternaleaf.",
      },
      // TODO - This discount code may need custom tracking. Please see - https://montugroup.atlassian.net/browse/LGA-429
      {
        type: 'custom',
        component: <AddDiscountCode />,
      },
    ],
    nextButtonConfig: {
      text: 'Book consultation now',
      metadata: {
        id: 'book-consultation',
      },
    },
  },
  {
    id: 9,
    formTitle: <MobileOnlyLogo />,
    type: 'section',
    pageTitle: 'Eligibility Result',
    title: 'Based on your provided information, you are currently ineligible for an Alternaleaf nurse consultation.',
    content: [
      {
        type: 'text',
        value:
          'Our guidelines ensure alternative healthcare is safe and appropriate for all patients. If you’d like further assistance, please **[contact us](https://www.alternaleaf.com.au/contact-us)** to get in touch with our support team.',
      },
    ],
    nextButtonConfig: {
      href: 'https://alternaleaf.com.au/',
      text: 'Return to Alternaleaf home',
    },
  },
];
